import React, { useState } from "react";
import { Box, Typography, Collapse, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import EventIcon from "@mui/icons-material/Event";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Emoji } from "emoji-picker-react";
import Button from "../input/button";
import {
  removeTask,
  setTaskTargeted as setTaskTargetedAction,
} from "../../redux/actions/tasks";
import { setOverlayVisible } from "../../redux/actions/overlay";
import useTaskAPIClient from "../../api/task/hooks/useTaskAPIClient";

const ReminderItem = ({
  reminder,
  onEdit,
  onComplete,
  completingTaskId,
  onTarget,
  loadingTargetId,
}) => {
  const isOverdue = reminder.timeUntil < 0;
  const isUrgent = reminder.timeUntil >= 0 && reminder.timeUntil <= 24;
  const isCompleting = completingTaskId === reminder.taskId;
  const isTargeting = loadingTargetId === reminder.taskId;

  const getTimeLabel = () => {
    if (isOverdue) {
      const hoursAgo = Math.abs(reminder.timeUntil);
      if (hoursAgo < 1) return "Just passed";
      if (hoursAgo < 24) return `${hoursAgo}h overdue`;
      return `${Math.round(hoursAgo / 24)} days overdue`;
    }
    if (reminder.timeUntil < 1) return "Due in less than 1h";
    if (reminder.timeUntil < 24) return `Due in ${reminder.timeUntil}h`;
    return `Due in ${Math.round(reminder.timeUntil / 24)} days`;
  };

  const getBgColor = () => {
    if (isOverdue) return "#cb5538";
    if (isUrgent) return "#c26229";
    return "#197b68";
  };

  const accentColor = getBgColor();

  return (
    <Box
      className="upcoming-reminders__item"
      sx={{
        borderRadius: "12px",
        marginBottom: "8px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
      }}
    >
      {/* Coloured title header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "10px 14px",
          background: accentColor,
          gap: "10px",
          borderRadius: "12px 12px 0 0",
        }}
      >
        {/* Icon circle — hidden below 440px */}
        <Box
          sx={{
            width: "32px",
            height: "32px",
            minWidth: "32px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            "@media (max-width: 439px)": { display: "none" },
          }}
        >
          {reminder.emoji ? (
            <Emoji unified={reminder.emoji} size={20} />
          ) : reminder.reminderType === "deadline" ? (
            <AccessAlarmIcon sx={{ color: "#fff", fontSize: "18px" }} />
          ) : (
            <EventIcon sx={{ color: "#fff", fontSize: "18px" }} />
          )}
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: "#fff",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {/* Inline emoji — shown below 440px only */}
          {reminder.emoji && (
            <Box
              component="span"
              sx={{
                display: "none",
                flexShrink: 0,
                lineHeight: 1,
                "@media (max-width: 439px)": { display: "inline-flex" },
              }}
            >
              <Emoji unified={reminder.emoji} size={16} />
            </Box>
          )}
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {reminder.name}
          </Box>
        </Typography>
      </Box>

      {/* White body: schedule/deadline info + action buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          background: "#fff",
          gap: "12px",
          borderRadius: "0 0 12px 12px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {reminder.reminderType === "deadline"
              ? "⏰ Deadline"
              : "📅 Scheduled"}
            {" • "}
            {reminder.reminderTime.format("ddd, MMM D [at] h:mm A")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isOverdue ? accentColor : "rgba(0,0,0,0.5)",
              fontWeight: isOverdue ? 700 : 400,
              fontSize: "0.7rem",
            }}
          >
            {getTimeLabel()}
          </Typography>
        </Box>
        <Box
          className="upcoming-reminders__item-actions"
          sx={{ display: "flex", gap: "8px", flexShrink: 0 }}
        >
          <Button
            text="Target"
            color="yellow"
            icon="target"
            collapsable={true}
            loading={isTargeting}
            onClick={() => onTarget(reminder.taskId)}
          />
          <Button
            text="Edit"
            color="orange"
            icon="edit"
            collapsable={true}
            onClick={() => onEdit(reminder.taskId)}
          />
          <Button
            text="Complete"
            color="green"
            icon="tick"
            collapsable={true}
            loading={isCompleting}
            onClick={() => onComplete(reminder.taskId)}
          />
        </Box>
      </Box>
    </Box>
  );
};

const UpcomingReminders = ({ reminders }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [loadingTargetId, setLoadingTargetId] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { completeTask, setTaskTargeted } = useTaskAPIClient();

  const handleEdit = (taskId) => {
    navigate("/edit-task", { state: { taskId } });
  };

  const handleTarget = async (taskId) => {
    setLoadingTargetId(taskId);
    try {
      dispatch(setTaskTargetedAction({ taskId, targeted: true }));
      await setTaskTargeted(taskId, true);
    } finally {
      setLoadingTargetId(null);
    }
  };

  const handleComplete = async (taskId) => {
    const onConfirmCallback = async () => {
      setCompletingTaskId(taskId);
      try {
        dispatch(removeTask(taskId));
        await completeTask(taskId);
      } finally {
        setCompletingTaskId(null);
      }
      dispatch(setOverlayVisible({ visible: false }));
    };

    dispatch(
      setOverlayVisible({
        visible: true,
        overlayType: "confirm",
        confirmationText: "Have you completed this task?",
        confirmCallback: onConfirmCallback,
      }),
    );
  };

  // Filter out targeted tasks from upcoming reminders
  const upcomingReminders = reminders.filter((r) => !r.targeted);

  const overdueCount = upcomingReminders.filter((r) => r.timeUntil < 0).length;
  const urgentCount = upcomingReminders.filter(
    (r) => r.timeUntil >= 0 && r.timeUntil <= 24,
  ).length;
  const hasWarning = overdueCount > 0 || urgentCount > 0;

  if (upcomingReminders.length === 0) {
    return null;
  }

  return (
    <Box
      className="upcoming-reminders"
      sx={{
        background: "rgba(255,255,255,0.95)",
        borderRadius: "20px",
        padding: "24px",
        marginBottom: "16px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
      }}
    >
      <Box
        onClick={() => setCollapsed(!collapsed)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: collapsed ? 0 : "16px",
          cursor: "pointer",
          userSelect: "none",
          transition: "margin-bottom 0.3s ease",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #cb5538 0%, #e06848 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
            }}
          >
            <AccessAlarmIcon sx={{ color: "#fff", fontSize: "20px" }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Upcoming
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
              {upcomingReminders.length} item
              {upcomingReminders.length !== 1 ? "s" : ""}
              {collapsed && overdueCount > 0
                ? ` • ${overdueCount} overdue`
                : ""}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {collapsed && hasWarning && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "#cb5538",
                boxShadow: "0 2px 12px rgba(203, 85, 56, 0.4)",
                animation: "pulseWarning 1.2s ease-in-out infinite",
                "@keyframes pulseWarning": {
                  "0%": {
                    boxShadow:
                      "0 2px 12px rgba(203, 85, 56, 0.4), 0 0 0 0 rgba(203, 85, 56, 0.7)",
                    transform: "scale(1)",
                  },
                  "50%": {
                    transform: "scale(1.1)",
                  },
                  "100%": {
                    boxShadow:
                      "0 2px 12px rgba(203, 85, 56, 0.4), 0 0 0 8px rgba(203, 85, 56, 0)",
                    transform: "scale(1)",
                  },
                },
              }}
            >
              <WarningAmberIcon
                sx={{
                  color: "#fff",
                  fontSize: "20px",
                }}
              />
            </Box>
          )}
          <IconButton
            size="small"
            sx={{
              background: "rgba(203, 85, 56, 0.1)",
              "&:hover": {
                background: "rgba(203, 85, 56, 0.2)",
              },
            }}
          >
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={!collapsed} timeout={300}>
        <Box className="upcoming-reminders__items">
          {upcomingReminders.map((reminder, index) => (
            <ReminderItem
              key={`${reminder.taskId}-${reminder.reminderType}-${index}`}
              reminder={reminder}
              onEdit={handleEdit}
              onComplete={handleComplete}
              completingTaskId={completingTaskId}
              onTarget={handleTarget}
              loadingTargetId={loadingTargetId}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default UpcomingReminders;
